import {Card, type FormControl} from 'platform/components';
import {Box, Hide, HStack, Integer, Show, VStack} from 'platform/foundation';

import {UseFormReturn} from 'react-hook-form';
import {Outlet} from 'react-router-dom';

import {suffixTestId, TestIdProps} from 'shared/web';

import {FilterContextType} from '../../../hooks/FilterContext';
import {useHeaderHeight} from '../../../hooks/useHeaderHeight';
import {useSavedFiltersVisibility} from '../../../hooks/useSavedFiltersVisibility';
import {FilterFormData} from '../../../types/FilterFormData';
import {PriceReportType} from '../../../types/PriceReportType';
import {useRelativeFilterPresetsController} from '../../Filter/components/SavedFilters/hooks/useRelativeFilterPresetsController';
import {SavedFilters} from '../../Filter/components/SavedFilters/SavedFilters';
import {Filter} from '../../Filter/Filter';
import {useSavedFiltersPanelMeasureRef} from '../../SavedFiltersPanel/hooks/SavedFiltersPanelHeightProvider';
import {SavedFiltersPanel} from '../../SavedFiltersPanel/SavedFiltersPanel';
import {StickyContainer} from '../../StickyContainer/StickyContainer';
import {useLookupDefaultAddress} from '../hooks/useLookupDefaultAddress';
import {getDefaultValuesWithResolvedAddress} from '../utils/getDefaultValuesWithResolvedAddress';
import {FilterModal} from './FilterModal';

type FilterLayoutFormProps = TestIdProps & {
  clearDebug: () => void;
  context: FilterContextType;
  control: FormControl<FilterFormData>;
  formApi: UseFormReturn<FilterFormData>;
  onSelectFilters: (filters: FilterFormData | null) => void;
  priceReport: PriceReportType;
};

export function FilterLayoutForm(props: FilterLayoutFormProps) {
  const headerHeight = useHeaderHeight();
  const {showSavedFilters, showSavedFiltersPanel} = useSavedFiltersVisibility();
  const panelMeasureRef = useSavedFiltersPanelMeasureRef();
  const presetController = useRelativeFilterPresetsController({
    enabled: showSavedFilters,
    priceReport: props.priceReport,
    formApi: props.formApi,
    onFilterChange: () => props.control.onChange?.(),
  });
  const resolvedDefaultAddress = useLookupDefaultAddress({
    formApi: props.formApi,
    priceReport: props.priceReport,
  });

  const defaultValues = getDefaultValuesWithResolvedAddress(
    props.priceReport,
    resolvedDefaultAddress
  );

  const onReset = () => {
    props.context.setErrors(null);
    props.onSelectFilters(null);
    props.clearDebug();
    props.formApi.reset(defaultValues);

    if (showSavedFilters && presetController.applyDefaultPresetIfAvailable()) {
      return;
    }

    props.control.onChange?.();
  };

  const isResetDisabled = showSavedFilters && presetController.isDefaultPresetBootstrapPending;

  return (
    <>
      <Show when={showSavedFiltersPanel}>
        {/* Measured wrapper — StatisticsPage subtracts the panel height from its viewport-sized chart card */}
        <div ref={panelMeasureRef}>
          {/* Bottom padding only on mobile/tablet — there the card would otherwise
              touch the filter bar; on desktop the statistics card provides the gap */}
          <Box padding={2} paddingBottom={[2, 2, 0, 0]}>
            <SavedFiltersPanel
              presetController={presetController}
              priceReport={props.priceReport}
              formApi={props.formApi}
              data-testid={suffixTestId('savedFiltersPanel', props)}
            />
          </Box>
        </div>
      </Show>
      <HStack>
        <Hide onMobile onTablet>
          <Box width={80} flexShrink={0} padding={2} paddingRight={0}>
            <StickyContainer offset={2}>
              <VStack spacing={3}>
                {/* The sidebar dropdown is superseded by the panel — show it only when the panel is off */}
                <Show when={showSavedFilters && !showSavedFiltersPanel}>
                  <Card>
                    <SavedFilters
                      presetController={presetController}
                      data-testid={suffixTestId('savedFilters', props)}
                    />
                  </Card>
                </Show>
                <Card>
                  <Filter
                    priceReport={props.priceReport}
                    formControl={props.control}
                    formApi={props.formApi}
                    onReset={onReset}
                    hasDefaultPreset={presetController.hasDefaultPreset}
                    isResetDisabled={isResetDisabled}
                    data-testid="filter"
                  />
                </Card>
              </VStack>
            </StickyContainer>
          </Box>
        </Hide>
        <Box flexGrow={1} minWidth={0}>
          <Box
            position={[undefined, undefined, 'sticky']}
            top={(headerHeight / 4) as Integer}
            data-testid={suffixTestId('stickyContainer', props)}
          >
            <Outlet />
          </Box>
        </Box>
      </HStack>
      <Show onMobile onTablet>
        <FilterModal
          priceReport={props.priceReport}
          formControl={props.control}
          formApi={props.formApi}
          onReset={onReset}
          showSavedFilters={showSavedFilters}
          presetController={presetController}
          isResetDisabled={isResetDisabled}
          data-testid="filterModal"
        />
      </Show>
    </>
  );
}
